/**
 * The web half of `StatusMap.kt`.
 *
 * CLAUDE.md rule 3: all status rendering goes through one map, no screen may `switch` on a raw status string,
 * and **colour is never the only signal** — every status renders colour + icon + label. That rule exists
 * because a red pill and an amber pill are indistinguishable to a colour-blind supervisor and to anyone
 * reading a screen in direct sunlight, which is the normal condition in a plant.
 *
 * Kept in step with the Android map by value, including the two-way aliases (`COMPLIANCE_OVERDUE` and
 * `OVERDUE` resolve identically) that the app already handles. An unknown status falls back to a neutral pill
 * with the raw value shown rather than throwing: the fleet once rendered entirely as lower-cased enum names
 * because a fallback silently swallowed everything, so the fallback here is visible on purpose.
 *
 * Icons are inline SVG paths rather than an icon font: no extra request, no FOUT, and they inherit
 * `currentColor` so a pill's icon and text cannot drift apart.
 */

export const KIND = {
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'danger',
  INFO: 'info',
  NEUTRAL: 'neutral',
  SAFETY: 'safety',
  OFFLINE: 'offline',
}

/* Material-style glyphs, trimmed to a single path each. */
const I = {
  new: 'M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zM8 15H6.5v-2.5h-2V15H3V9h1.5v2h2V9H8v6zm4.5 0H10V9h2.5c.83 0 1.5.67 1.5 1.5v3c0 .83-.67 1.5-1.5 1.5zm8.5-4.5H19V12h1v1.5h-1V15h2v1.5h-3.5V9H21v1.5z',
  person: 'M12 4a4 4 0 110 8 4 4 0 010-8zm0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4z',
  read: 'M20 2H4a2 2 0 00-2 2v18l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2zm-9 12H9v-2h2v2zm0-4H9V6h2v4zm4 4h-2v-2h2v2zm0-4h-2V6h2v4z',
  gear: 'M19.4 13a7.6 7.6 0 000-2l2.1-1.6-2-3.5-2.5 1a7.7 7.7 0 00-1.7-1L15 3H9.9l-.3 2.9c-.6.3-1.2.6-1.7 1l-2.5-1-2 3.5L5.5 11a7.6 7.6 0 000 2l-2.1 1.6 2 3.5 2.5-1c.5.4 1.1.7 1.7 1l.3 2.9H15l.3-2.9c.6-.3 1.2-.6 1.7-1l2.5 1 2-3.5L19.4 13zM12.5 15a3 3 0 110-6 3 3 0 010 6z',
  pause: 'M12 2a10 10 0 100 20 10 10 0 000-20zm-1 13H9V9h2v6zm4 0h-2V9h2v6z',
  check: 'M12 2a10 10 0 100 20 10 10 0 000-20zm-1 15l-5-5 1.4-1.4L11 14.2l5.6-5.6L18 10l-7 7z',
  lock: 'M18 8h-1V6a5 5 0 00-10 0v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2zm-6 9a2 2 0 110-4 2 2 0 010 4zM9 8V6a3 3 0 016 0v2H9z',
  replay: 'M12 5V1L7 6l5 5V7a5 5 0 11-5 5H5a7 7 0 107-7z',
  clock: 'M12 2a10 10 0 100 20 10 10 0 000-20zm1 11h-4v-2h2V7h2v6z',
  cloudUp: 'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A6 6 0 006 20h13a5 5 0 00.35-9.96zM14 13v4h-4v-4H7l5-5 5 5h-3z',
  cloudDone: 'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A6 6 0 006 20h13a5 5 0 00.35-9.96zM10 17l-3.5-3.5 1.4-1.4L10 14.2l5.6-5.6 1.4 1.4L10 17z',
  cloudOff: 'M24 15a4 4 0 00-3.35-3.96A7.49 7.49 0 009.73 5.6l2.1 2.1A5.5 5.5 0 0117 12h2a2.5 2.5 0 010 5H8.83l2 2H19a4 4 0 005-4zM4.41 3.86L3 5.27l2.77 2.77A6 6 0 006 20h11.73l2 2 1.41-1.41L4.41 3.86z',
  warn: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
  build: 'M22.7 19l-9.1-9.1a5.5 5.5 0 00-7.1-7.1l3.5 3.5-2.1 2.1L4.4 4.9a5.5 5.5 0 007.1 7.1l9.1 9.1 2.1-2.1z',
  low: 'M14 5h8v2h-8V5zm0 6h8v2h-8v-2zm0 6h8v2h-8v-2zM2 11h8v2H2v-2z',
  block: 'M12 2a10 10 0 100 20 10 10 0 000-20zM4 12a8 8 0 0112.9-6.3L5.7 16.9A7.9 7.9 0 014 12zm8 8a7.9 7.9 0 01-4.9-1.7L18.3 7.1A8 8 0 0112 20z',
  remove: 'M12 2a10 10 0 100 20 10 10 0 000-20zm5 11H7v-2h10v2z',
  shieldOk: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 15l-4-4 1.4-1.4L11 13.2l5.6-5.6L18 9l-7 7z',
  shieldBad: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm1 15h-2v-2h2v2zm0-4h-2V7h2v5z',
  timer: 'M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42a11 11 0 00-1.41-1.41l-1.42 1.42A9 9 0 1012 21a9 9 0 007.03-13.61z',
  search: 'M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1114 9.5 4.5 4.5 0 019.5 14z',
  verified: 'M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z',
  offline: 'M23.64 7a13 13 0 00-3.09-2.13l-1.5 1.51A11 11 0 0122 8.6l1.64-1.6zM3.41 1.31L2 2.72l2.05 2.05A13 13 0 00.36 7L2 8.6a11 11 0 013.5-2.24l2.2 2.2A8 8 0 004.5 10.5L6 12a6 6 0 013.1-1.64l2.1 2.1A4 4 0 009 14l3 3 1.6-1.6L20.7 22l1.4-1.41L3.41 1.31z',
}

/** status string -> { kind, icon, label }. Aliases share a single definition. */
const MAP = {
  // Complaint lifecycle
  NEW: [KIND.INFO, I.new, 'New'],
  ASSIGNED: [KIND.INFO, I.person, 'Assigned'],
  ACKNOWLEDGED: [KIND.INFO, I.read, 'Acknowledged'],
  IN_PROGRESS: [KIND.WARNING, I.gear, 'In progress'],
  ON_HOLD: [KIND.WARNING, I.pause, 'On hold'],
  RESOLVED: [KIND.SUCCESS, I.check, 'Resolved'],
  CLOSED: [KIND.NEUTRAL, I.lock, 'Closed'],
  REOPENED: [KIND.DANGER, I.replay, 'Reopened'],
  REJECTED: [KIND.DANGER, I.remove, 'Rejected'],
  AWAITING_CONFIRMATION: [KIND.WARNING, I.clock, 'Awaiting confirmation'],

  // Sync (shown when the console reads an outbox state from the app's data)
  PENDING_SYNC: [KIND.WARNING, I.clock, 'Waiting to send'],
  SENDING: [KIND.INFO, I.cloudUp, 'Sending'],
  SYNCED: [KIND.SUCCESS, I.cloudDone, 'Sent'],
  FAILED: [KIND.DANGER, I.cloudOff, 'Failed'],

  // Priority
  SAFETY: [KIND.DANGER, I.warn, 'Safety'],
  BREAKDOWN: [KIND.WARNING, I.build, 'Breakdown'],
  MINOR: [KIND.NEUTRAL, I.low, 'Minor'],

  // Asset status
  ACTIVE: [KIND.SUCCESS, I.check, 'Active'],
  UNDER_MAINTENANCE: [KIND.WARNING, I.gear, 'Under maintenance'],
  LOCKED_OUT: [KIND.SAFETY, I.block, 'Locked out'],
  DECOMMISSIONED: [KIND.NEUTRAL, I.remove, 'Decommissioned'],

  // Compliance — the alias pairs the app also carries
  VALID: [KIND.SUCCESS, I.shieldOk, 'Valid'],
  COMPLIANCE_VALID: [KIND.SUCCESS, I.shieldOk, 'Valid'],
  DUE_SOON: [KIND.WARNING, I.timer, 'Due soon'],
  COMPLIANCE_DUE_SOON: [KIND.WARNING, I.timer, 'Due soon'],
  OVERDUE: [KIND.DANGER, I.shieldBad, 'Overdue'],
  COMPLIANCE_OVERDUE: [KIND.DANGER, I.shieldBad, 'Overdue'],
  NEVER_TESTED: [KIND.DANGER, I.shieldBad, 'Never tested'],

  // Incident lifecycle
  REPORTED: [KIND.INFO, I.new, 'Reported'],
  INVESTIGATING: [KIND.WARNING, I.search, 'Investigating'],
  INVESTIGATED: [KIND.INFO, I.read, 'Investigated'],
  CAPA: [KIND.WARNING, I.gear, 'CAPA open'],
  VERIFIED: [KIND.SUCCESS, I.verified, 'Verified'],

  // CAPA
  OPEN: [KIND.WARNING, I.clock, 'Open'],
  DONE: [KIND.INFO, I.check, 'Done'],
}

/**
 * Resolve any raw status to its style.
 *
 * Unknown values return a neutral pill carrying the **raw string**, not a blank or a guess: if the server
 * introduces a status the console has not learned, a supervisor should see `SOMETHING_NEW` and be able to
 * report it, rather than see nothing and assume the record is fine.
 */
export function statusStyle(raw) {
  if (!raw) return { kind: KIND.NEUTRAL, icon: I.low, label: '—', known: false }
  const hit = MAP[String(raw).toUpperCase()]
  if (!hit) return { kind: KIND.NEUTRAL, icon: I.low, label: String(raw), known: false }
  return { kind: hit[0], icon: hit[1], label: hit[2], known: true }
}

export const ICONS = I
